import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Switch, message, Divider, Space, Tag, Typography, InputNumber } from 'antd';
import { MailOutlined, SaveOutlined, SendOutlined, SettingOutlined, SafetyOutlined, WhatsAppOutlined } from '@ant-design/icons';
import api from '../services/api';
import {
  useGetWhatsAppConfigQuery,
  useSaveWhatsAppConfigMutation,
  useTestWhatsAppMutation,
} from '../store/api';

const { Title, Text } = Typography;

const AdminSMTP = () => {
  const [form] = Form.useForm();
  const [waForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [twofaEnabled, setTwofaEnabled] = useState(true);
  const [toggling2FA, setToggling2FA] = useState(false);
  const { data: waConfig, refetch: refetchWa } = useGetWhatsAppConfigQuery();
  const [saveWa, { isLoading: savingWa }] = useSaveWhatsAppConfigMutation();
  const [testWa, { isLoading: testingWa }] = useTestWhatsAppMutation();

  useEffect(() => {
    if (waConfig) {
      waForm.setFieldsValue({
        whatsapp_access_token: '',
        whatsapp_phone_number_id: waConfig.whatsapp_phone_number_id || '',
        whatsapp_recipient: waConfig.whatsapp_recipient || '',
        whatsapp_api_version: waConfig.whatsapp_api_version || 'v23.0',
        whatsapp_template_name: waConfig.whatsapp_template_name || '',
        whatsapp_template_lang: waConfig.whatsapp_template_lang || 'es',
      });
    }
  }, [waConfig, waForm]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/smtp-config');
      if (data.config) {
        form.setFieldsValue({
          smtp_host: data.config.smtp_host || '',
          smtp_port: parseInt(data.config.smtp_port) || 587,
          smtp_user: data.config.smtp_user || '',
          smtp_pass: data.config.smtp_pass || '',
          smtp_from_email: data.config.smtp_from_email || '',
          smtp_from_name: data.config.smtp_from_name || '',
          smtp_encryption: data.config.smtp_encryption || 'tls',
        });
      }
    } catch {
      message.error('Error al cargar la configuración');
    }
    setLoading(false);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await api.post('/admin/smtp-config', values);
      message.success('Configuración SMTP guardada');
    } catch {
      message.error('Error al guardar');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testEmail) { message.warning('Ingresa un email de prueba'); return; }
    setTesting(true);
    try {
      const { data } = await api.post('/admin/smtp-test', { email: testEmail });
      if (data.success) {
        message.success(data.message);
      } else {
        message.error(data.message || 'Error al enviar');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al enviar email de prueba');
    }
    setTesting(false);
  };

  const handleToggle2FA = async (enabled) => {
    setToggling2FA(true);
    try {
      await api.post('/admin/toggle-2fa', { enabled });
      setTwofaEnabled(enabled);
      message.success(`2FA ${enabled ? 'activado' : 'desactivado'}`);
    } catch {
      message.error('Error al cambiar el estado de 2FA');
    }
    setToggling2FA(false);
  };

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #e5e9f2',
    borderRadius: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  };

  return (
    <div className="page-container fade-in">
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ color: '#001737', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <SettingOutlined style={{ color: '#0168fa' }} /> Configuración SMTP & 2FA
        </Title>
        <Text style={{ color: '#596882' }}>Configura el servidor de correo y la autenticación de dos factores</Text>
      </div>

      {/* 2FA Toggle */}
      <Card style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SafetyOutlined style={{ fontSize: 24, color: '#0168fa' }} />
            <div>
              <Text strong style={{ color: '#001737', fontSize: 15 }}>Autenticación de Dos Factores</Text>
              <br />
              <Text style={{ color: '#596882', fontSize: 12 }}>Envía un código por email al iniciar sesión</Text>
            </div>
          </div>
          <Space>
            <Tag color={twofaEnabled ? 'green' : 'default'}>{twofaEnabled ? 'Activo' : 'Inactivo'}</Tag>
            <Switch checked={twofaEnabled} onChange={handleToggle2FA} loading={toggling2FA} />
          </Space>
        </div>
      </Card>

      {/* SMTP Config */}
      <Card style={cardStyle} loading={loading}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <MailOutlined style={{ fontSize: 20, color: '#0168fa' }} />
          <Text strong style={{ color: '#001737', fontSize: 16 }}>Servidor SMTP</Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="smtp_host" label={<Text style={{ color: '#8392a5' }}>Host</Text>} rules={[{ required: true }]}>
              <Input placeholder="smtp.gmail.com" />
            </Form.Item>
            <Form.Item name="smtp_port" label={<Text style={{ color: '#8392a5' }}>Puerto</Text>} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} placeholder="587" />
            </Form.Item>
            <Form.Item name="smtp_user" label={<Text style={{ color: '#8392a5' }}>Usuario</Text>} rules={[{ required: true }]}>
              <Input placeholder="tu@email.com" />
            </Form.Item>
            <Form.Item name="smtp_pass" label={<Text style={{ color: '#8392a5' }}>Contraseña</Text>} rules={[{ required: true }]}>
              <Input.Password placeholder="App password" />
            </Form.Item>
            <Form.Item name="smtp_from_email" label={<Text style={{ color: '#8392a5' }}>Email remitente</Text>}>
              <Input placeholder="noreply@tudominio.com" />
            </Form.Item>
            <Form.Item name="smtp_from_name" label={<Text style={{ color: '#8392a5' }}>Nombre remitente</Text>}>
              <Input placeholder="SmartAdmin" />
            </Form.Item>
          </div>
          <Form.Item name="smtp_encryption" label={<Text style={{ color: '#8392a5' }}>Encriptación</Text>}>
            <Select options={[{ value: 'tls', label: 'TLS (587)' }, { value: 'ssl', label: 'SSL (465)' }, { value: 'none', label: 'Sin encriptación' }]} />
          </Form.Item>

          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}
            style={{ background: 'linear-gradient(135deg, #0168fa, #042893)', border: 'none', borderRadius: 8 }}>
            Guardar Configuración
          </Button>
        </Form>

        <Divider style={{ borderColor: '#e5e9f2' }} />

        {/* Test Email */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <SendOutlined style={{ color: '#0168fa' }} />
          <Text strong style={{ color: '#001737' }}>Enviar Email de Prueba</Text>
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <Input placeholder="email@ejemplo.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} style={{ flex: 1 }} />
          <Button type="primary" icon={<SendOutlined />} onClick={handleTest} loading={testing}
            style={{ background: '#0168fa', border: 'none' }}>
            Enviar
          </Button>
        </Space.Compact>

        <Divider style={{ borderColor: '#e5e9f2' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <WhatsAppOutlined style={{ color: '#25D366', fontSize: 18 }} />
          <Text strong style={{ color: '#001737' }}>Meta WhatsApp Cloud API</Text>
          {waConfig?.whatsapp_access_token_set ? (
            <Tag color="green">Token: {waConfig.whatsapp_access_token_mask}</Tag>
          ) : (
            <Tag>Sin token</Tag>
          )}
        </div>
        <Form
          form={waForm}
          layout="vertical"
          onFinish={async (values) => {
            try {
              const payload = {
                whatsapp_phone_number_id: values.whatsapp_phone_number_id || '',
                whatsapp_recipient: values.whatsapp_recipient || '',
                whatsapp_api_version: values.whatsapp_api_version || 'v23.0',
                whatsapp_template_name: values.whatsapp_template_name || '',
                whatsapp_template_lang: values.whatsapp_template_lang || 'es',
              };
              if (values.whatsapp_access_token) payload.whatsapp_access_token = values.whatsapp_access_token;
              await saveWa(payload).unwrap();
              message.success('WhatsApp guardado');
              refetchWa();
            } catch {
              message.error('No se pudo guardar WhatsApp');
            }
          }}
        >
          <Form.Item name="whatsapp_access_token" label={<Text style={{ color: '#8392a5' }}>Access token (dejar vacío para no cambiar)</Text>}>
            <Input.Password placeholder="EAAG..." />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="whatsapp_phone_number_id" label={<Text style={{ color: '#8392a5' }}>Phone number ID</Text>}>
              <Input placeholder="123456789012345" />
            </Form.Item>
            <Form.Item name="whatsapp_api_version" label={<Text style={{ color: '#8392a5' }}>Versión Graph API</Text>}>
              <Input placeholder="v23.0" />
            </Form.Item>
            <Form.Item name="whatsapp_recipient" label={<Text style={{ color: '#8392a5' }}>Destino por defecto</Text>}>
              <Input placeholder="573001234567" />
            </Form.Item>
            <Form.Item name="whatsapp_template_lang" label={<Text style={{ color: '#8392a5' }}>Idioma plantilla</Text>}>
              <Input placeholder="es" />
            </Form.Item>
          </div>
          <Form.Item
            name="whatsapp_template_name"
            label={<Text style={{ color: '#8392a5' }}>Plantilla aprobada (opcional)</Text>}
            extra="Se usa cuando Meta bloquea el texto libre por la ventana de 24 h. Debe tener una variable en el cuerpo ({{1}})."
          >
            <Input placeholder="alertas_smartadmin" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={savingWa} icon={<SaveOutlined />}>
              Guardar WhatsApp
            </Button>
            <Button
              loading={testingWa}
              icon={<SendOutlined />}
              onClick={async () => {
                try {
                  const res = await testWa().unwrap();
                  if (res.sent) message.success(`Mensaje de prueba enviado (${res.via})`);
                  else message.warning(res.error || 'No se envió (revisa token / phone number ID / destino)');
                } catch (e) {
                  message.error(e?.data?.error || 'Fallo la prueba de WhatsApp');
                }
              }}
            >
              Enviar prueba
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
    </div>
  );
};

export default AdminSMTP;
